import {SchemaPropertyType} from "@vesta/vos-types/modules/platformLevel/storageDatabases/domain/StorageDatabaseSchema";
import type {Iso8601DateTime, Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {iso8601DateTimeZodValidator, uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {getSchemaPropertyTypeFromUserDefinedObjectTemplateAdditionalColumnType, UserDefinedObjectTemplateAdditionalColumnType, UserDefinedObjectTemplateAdditionalColumnSourceType, userDefinedObjectTemplateAdditionalColumnsValidator, type UserDefinedObjectTemplateAdditionalColumn} from "@vesta/vos-types/modules/workspaceLevel/userDefinedObjects/domain/UserDefinedObjectTemplateAdditionalColumn";
import {z as zod} from "zod";

export enum UserDefinedObjectTemplateStatus {
    active = "eb1543b2-c28a-4496-b234-19df090eb11e",
    archived = "70e36f4a-7194-4af6-a377-635dfacb4226",
}

/** Enforced on current, non-deleted objects only. The UUID is also the storage index name. */
export type UserDefinedObjectTemplateUniqueConstraint = {
    id: Uuid;
    additionalColumnIds: Array<Uuid>;
};

export const userDefinedObjectTemplateUniqueConstraintsValidator: zod.ZodType<Array<UserDefinedObjectTemplateUniqueConstraint>> = zod.array(zod.strictObject({
    id: uuidZodValidator,
    additionalColumnIds: zod.array(uuidZodValidator).min(1),
})).superRefine((constraints, ctx) => {
    const ids = new Set<string>();
    const definitions = new Set<string>();
    for (const [index, constraint] of constraints.entries()) {
        const columns = constraint.additionalColumnIds.map(id => id.toLowerCase());
        const definition = [...columns].sort().join(",");
        if (ids.has(constraint.id.toLowerCase()) || definitions.has(definition) || new Set(columns).size !== columns.length) {
            ctx.addIssue({code: "custom", path: [index], message: "Unique constraints require distinct IDs, columns and column combinations"});
        }
        ids.add(constraint.id.toLowerCase());
        definitions.add(definition);
    }
});

/** Current objects may reference soft-deleted targets. Audit snapshots are never constrained. */
export type UserDefinedObjectTemplateForeignKey = {
    id: Uuid;
    additionalColumnId: Uuid;
    referencedUdoTemplateId: Uuid;
};

export const userDefinedObjectTemplateForeignKeysValidator: zod.ZodType<Array<UserDefinedObjectTemplateForeignKey>> = zod.array(zod.strictObject({
    id: uuidZodValidator,
    additionalColumnId: uuidZodValidator,
    referencedUdoTemplateId: uuidZodValidator,
})).superRefine((foreignKeys, ctx) => {
    const ids = new Set<string>();
    const columns = new Set<string>();
    for (const [index, foreignKey] of foreignKeys.entries()) {
        if (ids.has(foreignKey.id.toLowerCase()) || columns.has(foreignKey.additionalColumnId.toLowerCase())) {
            ctx.addIssue({code: "custom", path: [index], message: "Foreign keys require distinct IDs and source columns"});
        }
        ids.add(foreignKey.id.toLowerCase());
        columns.add(foreignKey.additionalColumnId.toLowerCase());
    }
});

export function validateUserDefinedObjectTemplateForeignKeys(
    additionalColumns: Array<UserDefinedObjectTemplateAdditionalColumn>,
    foreignKeys: Array<UserDefinedObjectTemplateForeignKey>,
): void {
    userDefinedObjectTemplateForeignKeysValidator.parse(foreignKeys);
    for (const foreignKey of foreignKeys) {
        const column = additionalColumns.find(column => column.id === foreignKey.additionalColumnId);
        if (column === undefined || column.source.type !== UserDefinedObjectTemplateAdditionalColumnSourceType.physical
            || getSchemaPropertyTypeFromUserDefinedObjectTemplateAdditionalColumnType(column.type) !== SchemaPropertyType.uuid) {
            throw new Error(`Foreign key ${foreignKey.id} requires an existing physical UUID additional column ${foreignKey.additionalColumnId}`);
        }
        if (column.type === UserDefinedObjectTemplateAdditionalColumnType.userDefinedObject
            && column.configuration.userDefinedObjectTemplateId !== foreignKey.referencedUdoTemplateId) {
            throw new Error(`Foreign key ${foreignKey.id} target disagrees with its additional column's existing UDO reference`);
        }
    }
}

export type UserDefinedObjectTemplateConfiguration = {
    uniqueConstraints: Array<UserDefinedObjectTemplateUniqueConstraint>;
    foreignKeys: Array<UserDefinedObjectTemplateForeignKey>;
};

export const userDefinedObjectTemplateConfigurationValidator: zod.ZodType<UserDefinedObjectTemplateConfiguration> = zod.strictObject({
    uniqueConstraints: userDefinedObjectTemplateUniqueConstraintsValidator,
    foreignKeys: userDefinedObjectTemplateForeignKeysValidator,
}).superRefine((configuration, ctx) => {
    const ids = new Set(configuration.uniqueConstraints.map(constraint => constraint.id.toLowerCase()));
    for (const foreignKey of configuration.foreignKeys) {
        if (ids.has(foreignKey.id.toLowerCase())) {
            ctx.addIssue({code: "custom", path: ["foreignKeys"], message: "Constraint IDs must be distinct across uniqueness and foreign keys"});
        }
    }
});

/** Used by API validation, storage DDL and local configuration validation. */
export function validateUserDefinedObjectTemplateUniqueConstraints(
    additionalColumns: Array<UserDefinedObjectTemplateAdditionalColumn>,
    uniqueConstraints: Array<UserDefinedObjectTemplateUniqueConstraint>,
): void {
    userDefinedObjectTemplateUniqueConstraintsValidator.parse(uniqueConstraints);
    const physicalIds = new Set(additionalColumns.filter(column => column.source.type === UserDefinedObjectTemplateAdditionalColumnSourceType.physical).map(column => column.id.toLowerCase()));
    for (const constraint of uniqueConstraints) {
        for (const id of constraint.additionalColumnIds) {
            if (!physicalIds.has(id.toLowerCase())) {
                throw new Error(`Unique constraint ${constraint.id} requires existing physical additional column ${id}`);
            }
        }
    }
}

export type UserDefinedObjectTemplate = {
    additionalColumns: Array<UserDefinedObjectTemplateAdditionalColumn>;
    configuration: UserDefinedObjectTemplateConfiguration;
    createdAt: Iso8601DateTime;
    createdBy: Uuid;
    description: string | null;
    displayName: string;
    id: Uuid;
    instanceDisplayNameTemplate: string;
    revisionId: Uuid;
    status: UserDefinedObjectTemplateStatus;
    updatedAt: Iso8601DateTime;
    updatedBy: Uuid;
    workspaceId: Uuid;
};

/** Each template audit row stores a full snapshot, identified by revisionId. */
export type UserDefinedObjectTemplateCudLog = UserDefinedObjectTemplate;

export const userDefinedObjectTemplateValidator: zod.ZodType<UserDefinedObjectTemplate> = zod.strictObject({
    additionalColumns: userDefinedObjectTemplateAdditionalColumnsValidator,
    configuration: userDefinedObjectTemplateConfigurationValidator,
    createdAt: iso8601DateTimeZodValidator,
    createdBy: uuidZodValidator,
    description: zod.string().nullable(),
    displayName: zod.string(),
    id: uuidZodValidator,
    instanceDisplayNameTemplate: zod.string().trim().min(1),
    revisionId: uuidZodValidator,
    status: zod.enum(UserDefinedObjectTemplateStatus),
    updatedAt: iso8601DateTimeZodValidator,
    updatedBy: uuidZodValidator,
    workspaceId: uuidZodValidator,
}).superRefine((template, ctx) => {
    try {
        validateUserDefinedObjectTemplateUniqueConstraints(template.additionalColumns, template.configuration.uniqueConstraints);
        validateUserDefinedObjectTemplateForeignKeys(template.additionalColumns, template.configuration.foreignKeys);
    } catch (error) {
        ctx.addIssue({code: "custom", path: ["configuration", "uniqueConstraints"], message: error instanceof Error ? error.message : String(error)});
    }
});
